// EditorV3.tsx
'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Button } from '@/components/ui/button'
import { Undo, Redo } from 'lucide-react'
import { Suggestions } from './modules/SuggestionsExtension'
import EditorToolbar from './modules/EditorToolbar'
import AIDialog from './modules/AIDialog'
import LinkDialog from './modules/LinkDialog'
import EditorBubbleMenu from './modules/EditorBubbleMenu'
import { realAIComplete } from './modules/mockAIComplete'
import { suggestionPluginKey } from './modules/SuggestionsExtension'
import { DecorationSet } from 'prosemirror-view'
import ImageResize from 'tiptap-extension-resize-image';

interface EditorV3Props {
  initialContent?: string;
  onContentChange?: (content: string) => void;
}

const EditorV3 = ({ initialContent = '', onContentChange }: EditorV3Props) => {
  const editorRef = useRef<Editor | null>(null)
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false)

  const handleToggleSuggestions = useCallback((enabled: boolean) => {
    setSuggestionsEnabled(enabled)
    // Immediately update plugin state
    if (editorRef.current) {
      const state = editorRef.current.state
      const tr = state.tr.setMeta(suggestionPluginKey, {
        ...suggestionPluginKey.getState(state),
        enabled,
        decorations: DecorationSet.empty,
        activeSuggestion: null,
        isLoading: false,
        startPosition: null,
      })
      editorRef.current.view.dispatch(tr)
    }
  }, [])


  const editor = useEditor({
    extensions: [
      StarterKit,
      ImageResize,
      Placeholder.configure({
        placeholder: 'Start typing or use the AI features...',
      }),
      Suggestions.configure({
        enabled: suggestionsEnabled,
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
        defaultAlignment: 'left',
      }),
      Image,
    ],
    content: initialContent, // Set the initial content here
    onUpdate: ({ editor }) => {
      const content = editor.getHTML()
      onContentChange?.(content)
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none h-[387px]',
      },
    },
  })

  useEffect(() => {
    if (editor) {
      editorRef.current = editor
      // Get the suggestions extension
      const extension = editor.extensionManager.extensions.find(
        ext => ext.name === 'suggestions'
      )
      
      if (extension) {
        // Force a plugin update
        editor.view.updateState(editor.view.state)
      }
    }
  }, [suggestionsEnabled, editor])

  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      .suggestion-widget {
        display: inline-block;
        color: #6B7280;
        background-color: #F3F4F6;
        padding: 0 4px;
        margin-left: 1px;
        font-style: italic;
        opacity: 0.8;
        position: relative;
        pointer-events: none;
      }

      .suggestion-loader {
        display: inline-block;
        color: #6B7280;
        animation: pulse 1s infinite;
        margin-left: 1px;
        pointer-events: none;
      }

      @keyframes pulse {
        0% { opacity: 0.3; }
        50% { opacity: 1; }
        100% { opacity: 0.3; }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  if (!editor) return null

  const handleImageUpload = () => {
    const url = window.prompt('Enter the URL of the image:')
    if (url) {
      editor.chain().focus().setImage({ src: url }).run()
    }
  }

  const handleLinkInsert = () => {
    // This will be handled by the LinkDialog component
  }

  const handleGetAISuggestions = async (prompt: string) => {
    try {
      // Pass null as type for general AI prompts
      const response = await realAIComplete(prompt, null)
      return response
    } catch (error) {
      console.error('Error getting AI response:', error)
      return ''
    }
  }

  return (
    <div className="border border-gray-300 h-[500px] rounded-md overflow-hidden">
      <div className="bg-gray-200 h-20 border-b border-gray-300 p-4 flex items-center justify-between flex-wrap gap-2">
        <EditorToolbar 
          editor={editor} 
          onImageUpload={handleImageUpload} 
          onLinkInsert={handleLinkInsert} 
          suggestionsEnabled={suggestionsEnabled}
          onToggleSuggestions={handleToggleSuggestions}
        />
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()}>
            <Undo className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()}>
            <Redo className="h-4 w-4" />
          </Button>
          <AIDialog onGetAISuggestions={handleGetAISuggestions} />
        </div>
      </div>
      <div className="bg-red p-4 overflow-x-hidden overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
      <LinkDialog editor={editor} />
      <EditorBubbleMenu editor={editor} />
    </div>
  )
}

export default EditorV3